import { useCallback, useEffect, useRef, useState } from "react";
import { joinAnalysisRoom, PEER_OF } from "../utils/analysisRoom";

/**
 * Joins the (matchId, analystId) analysis room as `role` and reports whether
 * the counterpart screen is present.
 *
 * `enabled` lets a screen stay out of the room until it actually matters
 * (e.g. the InputBoard only joins once the tagging view is open).
 */
const useAnalysisRoom = (matchId, analystId, role, enabled = true) => {
  const [members, setMembers] = useState([]);
  const [joined, setJoined] = useState(false);
  const [checking, setChecking] = useState(false);
  const refreshRef = useRef(null);
  const checkTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !matchId || !analystId || !role) {
      setMembers([]);
      setJoined(false);
      return;
    }

    let cancelled = false;
    let leave = null;

    joinAnalysisRoom(matchId, analystId, role, (list) => {
      if (!cancelled) setMembers(list);
    })
      .then((room) => {
        if (cancelled) {
          room && room.leave();
          return;
        }
        leave = room.leave;
        refreshRef.current = room.refresh;
        setJoined(true);
      })
      .catch((err) => console.error("Failed to join analysis room", err));

    return () => {
      cancelled = true;
      refreshRef.current = null;
      setJoined(false);
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
      if (leave) leave();
    };
  }, [matchId, analystId, role, enabled]);

  // Re-reads the presence set on demand. Presence events already keep this
  // current — Retry exists so the analyst can force the check themselves.
  const refresh = useCallback(() => {
    if (!refreshRef.current) return;
    setChecking(true);
    Promise.resolve(refreshRef.current())
      .catch((err) => console.error("Failed to refresh analysis room", err))
      .finally(() => {
        if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
        // Brief hold so the button state is visible on an instant response.
        checkTimerRef.current = setTimeout(() => setChecking(false), 600);
      });
  }, []);

  const peerRole = PEER_OF[role];
  const peerConnected = members.some((m) => m.role === peerRole);

  return { members, peerRole, peerConnected, joined, checking, refresh };
};

export default useAnalysisRoom;
